import { Box } from '@mui/material';
import { CatchBoundary, Outlet } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import Loader from './components/atoms/loader/loader';
import { appConfig } from './config';
import { useAuth } from './containers/contexts/auth-provider';
import { TechnicalBrake } from './containers/technical-brake';
import { useMeilisearchApiKey } from './hooks/api/use-meilisearch-api-key';
import { useConfirmJoinCurrentlySelectedSite } from './hooks/use-confirm-join-currently-selected-site';
import { useConfirmJoinCurrentlySelectedTenant } from './hooks/use-confirm-join-currently-selected-tenant';
import { useHasViewContextAccess } from './hooks/use-has-view-context-access';
import { useSetInitialOrganizations } from './hooks/use-set-initial-organizations';
import { AbilityProvider } from './permissions/can';
import { useUserStore } from './store/user-store';
import { UserRole } from './types/enum/user-role';

function App() {
  const auth = useAuth();
  const user = useUserStore();
  const isLoggedIn = !!auth.session?.access_token;

  const {
    tenantContext: tenantContextAccess,
    siteContext: siteContextAccess,
    hasFetchedAccess,
  } = useHasViewContextAccess();

  const { isLoadingAvailableTenants, isLoadingAvailableSites } =
    useSetInitialOrganizations(isLoggedIn && hasFetchedAccess);

  useConfirmJoinCurrentlySelectedTenant(
    user.viewContext === 'tenant' &&
      !isLoadingAvailableTenants &&
      isLoggedIn &&
      tenantContextAccess,
  );

  useConfirmJoinCurrentlySelectedSite(
    user.viewContext === 'site' &&
      !isLoadingAvailableSites &&
      isLoggedIn &&
      siteContextAccess,
  );

  useMeilisearchApiKey(
    isLoggedIn && hasFetchedAccess && !isLoadingAvailableTenants,
  );

  const renderContent = () => {
    if (auth.loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}>
          <Loader />
        </Box>
      );
    }

    return <Outlet />;
  };

  return (
    <CatchBoundary
      getResetKey={() => 'reset-key'}
      errorComponent={TechnicalBrake}>
      <AbilityProvider
        viewContext={user.viewContext ?? 'site'}
        role={user?.role ?? UserRole.VIEWER}
        userToken={user?.adminToken ?? ''}
        isSSOUser={user?.isSSO}>
        <Helmet>
          <title>{appConfig.name}</title>
        </Helmet>

        {renderContent()}
      </AbilityProvider>
    </CatchBoundary>
  );
}

export default App;
